import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// components
import Breadcrumbs from '../../../components/breadcrumbs';
import { Label, TextField, TextFields, SelectField, MultipleFields, Message as FormMessage } from '../../../components/hook-form';
import Dialog from '../../../components/dialog';
// @form
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @devextreme
import { Popover } from 'devextreme-react/popover';
import DataGrid, { Column } from 'devextreme-react/data-grid';
// assets
import { ReactComponent as Create } from '../../../assets/button/create.svg';
import { ReactComponent as Edit } from '../../../assets/button/edit.svg';
// utils
import { REGEXP_DECIMAL } from '../../../utils/regexp';
// styles
import { Container, StateTitle, SensorFormWapper, Tabs, Box, SelectButton, SearchPopover, ButtonWrapper, IconButton, PrimaryButton, TertiaryButton, Message } from '../../../styles';
// apis
import { getSensors, searchSensorsModel, updateChannels, createSensors, updateSensors, deleteSensors } from '../../../apis';

// ----------------------------------------------------------------------

const schema = yup.object().shape({
  waterQualityItem: yup.string(),
  alarmType: yup.string(),
  alarmLowerLimit: yup.number()
    .test('matches', '숫자, 소수점 3자리까지만 입력 가능합니다.', (value, context) => {
      if ( (context.parent.alarmType === 'OUT_OF_RANGE' || context.parent.alarmType === 'LOWER_LIMIT') && !REGEXP_DECIMAL.test(String(value)) ) return false;
      return true;
    }),
  alarmUpperLimit: yup.number()
    .test('matches', '숫자, 소수점 3자리까지만 입력 가능합니다.', (value, context) => {
      if ( (context.parent.alarmType === 'OUT_OF_RANGE' || context.parent.alarmType === 'UPPER_LIMIT') && !REGEXP_DECIMAL.test(String(value)) ) return false;
      return true;
    })
});

// ----------------------------------------------------------------------

interface Props {
  waterQualityItem?: string;
  alarmType?: string;
  alarmLowerLimit?: number;
  alarmUpperLimit?: number;
};

const defaultChannels = [ 1, 2, 3, 4, 5, 6 ];

// ----------------------------------------------------------------------

const ALARM: {[key: string]: string} = {
  '범위': 'OUT_OF_RANGE',
  '하한': 'LOWER_LIMIT',
  '상한': 'UPPER_LIMIT',
  '알람 정지': 'OFF'
};

// ----------------------------------------------------------------------

export default function SensorsPage() {
  const navigate  = useNavigate();
  const state = useLocation().state?.answer;

  const {
    control,
    setValue,
    getValues,
    watch,
    trigger,
    formState,
    clearErrors,
    handleSubmit
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      waterQualityItem: 'PH',
      alarmType: 'OUT_OF_RANGE',
      alarmLowerLimit: 0,
      alarmUpperLimit: 0
    }
  });
  
  const [ data, setData ] = useState([]);
  const [ tabs, setTabs ] = useState([]);
  const [ tab, setTab ] = useState(null);
  const [ previousTab, setPreviousTab ] = useState(null);

  const [ modelVisible, setModelVisible ] = useState(false);
  const [ models, setModels ] = useState({ content: [] });
  const [ model, setModel ] = useState(null);
  const [ previousModel, setPreviousModel ] = useState(null);
  
  const [ channel, setChannel ] = useState({ disabled: true, value: '', options: [] });
  const [ options, setOptions ] = useState(defaultChannels);
  
  const [ formMessage, setFormMessage ] = useState(null);
  const [ message, setMessage ] = useState(null);

  const [ channels, setChannels ] = useState<{[key: string]: string}>({});
  const [ channelMessage, setChannelMessage ] = useState(null);
  const [ channelDialog, setChannelDialog ] = useState(false);
  const closeChannelDialog = () => setChannelDialog(false);

  const [ messageDialog, setMessageDialog ] = useState(null);
  const closeMessageDialog = () => setMessageDialog(null);

  const [ addConfirm, setAddConfirm ] = useState(null);
  const closeAddConfirm = () => setAddConfirm(null);

  const [ tabConfirm, setTabConfirm ] = useState(null);
  const closeTabConfirm = () => setTabConfirm(null);

  const [ modelConfirm, setModelConfirm ] = useState(null);
  const closeModelConfirm = () => setModelConfirm(null);

  useEffect(() => {
    if ( !state ) return navigate(-1);
    resetSensors();
    ( async () =>
      await searchSensorsModel(setModels)
        .catch(() => {
          setModels({ content: [] });
        })
    )();
  }, [state]);

  useEffect(() => {
    if ( tab ) {
      if ( models.content.length > 0 && tab.waterQualitySensorId ) {
        const sensorModel = models.content.filter((item) => { return item.name === tab.name && item.model === tab.model });
        if ( sensorModel.length > 0 ) {
          const value = { ...tab, waterQualitySensorModelId: sensorModel[0].waterQualitySensorModelId};
          setModel(value);
          setFormValues(value);
          setPreviousModel(value);
        }
      }
      if ( tab.waterQualitySensorId ) {
        setChannel({ disabled: true, value: tab.channel, options: [tab.channel] });
      } else {
        setChannel({ disabled: false, value: String(options[0]), options: options });
      }
      setPreviousTab(tab);
    }
  }, [tab, models]);

  useEffect(() => {
    trigger();
  }, [watch('alarmType')]);

  useEffect(() => {
    if ( Object.values(formState.errors).filter((error) => error.type === 'matches').length > 0 ) return setFormMessage('숫자, 소수점 3자리까지만 입력 가능합니다.');
    setFormMessage(null);
  }, [formState]);

  useEffect(() => {
    if ( Object.values(formState.errors).length === 0 ) {
      if ( watch('alarmType') === 'OUT_OF_RANGE' && watch('alarmLowerLimit') >= watch('alarmUpperLimit') ) {
        setFormMessage('범위 설정을 확인해 주세요.');
      } else {
        setFormMessage(null);
      }
    }
  }, [watch()]);

  const setFormValues = (value: any) => {
    setValue('waterQualityItem', value.waterQualityItem);
    setValue('alarmLowerLimit', value.alarmLowerLimit === null ? 0 : value.alarmLowerLimit);
    setValue('alarmUpperLimit', value.alarmUpperLimit === null ? 0 : value.alarmUpperLimit);
    setValue('alarmType', value.alarmType || 'OUT_OF_RANGE');
  };
  
  const resetSensors = async () => {
    await getSensors(state.iotDeviceId)
    .then((response) => {
      setData(response);
      setTabs(response);
      if ( response.length > 0 ) {
        setTab(response[0]);
        setOptions( defaultChannels.filter((element) => !response.map((item: any) => item.channel).includes(element)) );
        const channel: {[key: string]: string} = {};
        response.forEach((element: any) => {
          channel[element.waterQualitySensorId] = element.channel;
        });
        setChannels(channel);
      } else {
        setTab(null);
      }
    })
    .catch(() => {
      setData([]);
      setTabs([]);
      setTab(null);
    });
  };

  const handleModel = (item: any) => {
    const alarmType = getValues('alarmType');
    const alarmLowerLimit = getValues('alarmLowerLimit');
    const alarmUpperLimit = getValues('alarmUpperLimit');

    if (
      previousModel.alarmType !== alarmType
      || (previousModel.alarmLowerLimit !== null && previousModel.alarmLowerLimit !== alarmLowerLimit)
      || (previousModel.alarmUpperLimit !== null && previousModel.alarmUpperLimit !== alarmUpperLimit)
    ) {
      setModelVisible(false);
      return setModelConfirm(item);
    } else {
      setModel(item);
      setFormValues({ ...item, alarmType: ALARM[item.alarmType]});
      setPreviousModel({ ...item, alarmType: ALARM[item.alarmType]});
      setModelVisible(false);
    };
  };

  const handleModelConfirm = () => {
    changeModel();
    setModelConfirm(null);
  };

  const changeModel = () => {
    setModel(modelConfirm);
    setFormValues(modelConfirm)
    setPreviousModel({ ...modelConfirm, alarmType: ALARM[modelConfirm.alarmType]});
    setModelVisible(false);
  };
  
  const handleTab = (item: any) => {
    const alarmType = getValues('alarmType');
    const alarmLowerLimit = getValues('alarmLowerLimit');
    const alarmUpperLimit = getValues('alarmUpperLimit');

    if ( previousTab && (
      previousTab.alarmType !== alarmType
      || (previousTab.alarmLowerLimit !== null && previousTab.alarmLowerLimit !== alarmLowerLimit)
      || (previousTab.alarmUpperLimit !== null && previousTab.alarmUpperLimit !== alarmUpperLimit)
    )) {
      return setTabConfirm({text: '이동하시면 작성중인 데이터는 저장이 되지 않습니다.\n이동하시겠습니까?', value: item });
    }
    setTab(item);
    setModel(item);
    setPreviousModel(item);
    setFormValues(item);
  };

  const handleAdd = () => {
    const alarmType = getValues('alarmType');
    const alarmLowerLimit = getValues('alarmLowerLimit');
    const alarmUpperLimit = getValues('alarmUpperLimit');

    if ( previousTab && (
      previousTab.alarmType !== alarmType
      || (previousTab.alarmLowerLimit !== null && previousTab.alarmLowerLimit !== alarmLowerLimit)
      || (previousTab.alarmUpperLimit !== null && previousTab.alarmUpperLimit !== alarmUpperLimit)
    )) {
      return setAddConfirm('센서를 추가하시면 작성중인 데이터는 저장이 되지 않습니다.\n이동하시겠습니까?');
    }
    if ( tabs.filter((element) => !element.waterQualitySensorId).length > 0 ) {
      return setMessageDialog('작성중인 추가 센서가 있습니다.\n저장을 완료하신 후 센서 추가를 진행해 주세요.');
    }

    addTab();
  };

  const addTab = () => {
    const num = tabs.map((element) => element.id);
    const max = num.length > 0 ? Math.max(...num) : 0;

    let values = { id: max + 1, alarmType: 'OUT_OF_RANGE', alarmLowerLimit: 0, alarmUpperLimit: 0 };
    if ( models.content.length > 0 ) {
      values = { ...values, ...models.content[0], alarmType: ALARM[models.content[0].alarmType] };
      setModel(values);
      setFormValues(values);
      setPreviousModel(values);
    }
    setTabs([...tabs, values]);
    setTab(values);
  };

  const [ deleteDialog, setDeleteDialog ] = useState(null);
  const [ deleteMessage, setdeleteMessage ] = useState(null);
  const closeDeleteDialog = () => {
    setDeleteDialog(null);
    setdeleteMessage(null);
  };

  const handleDelete = async () => {
    if ( !tab.waterQualitySensorId ) {
      const contents = tabs.filter((element) => element.id !== tab.id);
      setTabs(contents);
      setTab( contents.length > 0 ? contents[0] : null );
      closeDeleteDialog();
      setdeleteMessage(null);
    } else {
      await deleteSensors(tab.waterQualitySensorId)
        .then(() => {
          resetSensors();
          closeDeleteDialog();
          setdeleteMessage(null);
        })
        .catch(() => {
          setdeleteMessage('요청하신 수질 센서를 삭제할 수 없습니다. 나중에 다시 시도해 주세요.');
        });
    }
  };

  const handleChannel = async () => {
    const values = Object.values(channels);
    for ( let i = 0; i < values.length; i++ ) {
      if ( !/^[1-6]{1}$/g.test(values[i]) ) return setChannelMessage('1~6 사이 정수만 입력해 주세요.');
    }
    const filter = values.filter((element, index) => values.indexOf(element) !== index);
    if ( filter.length > 0 ) return setChannelMessage('중복된 채널이 있습니다. 다시 한 번 확인해 주세요.');

    const keys = Object.keys(channels);
    const result = [];
    for ( let i = 0; i < keys.length; i++ ) {
      result.push({ waterQualitySensorId: keys[i], channel: channels[keys[i]] });
    }

    await updateChannels(state.iotDeviceId, result)
      .then(() => {
        resetSensors();
        setChannelDialog(false);
      })
      .catch(() => setChannelMessage('채널을 수정할 수 없습니다. 나중에 다시 시도해 주세요.'));
  };

  const cellRender = ({data}: any) => {
    return <TextFields type='number' defaultValue={data.channel} onChange={(event) => setChannels({...channels, [data.waterQualitySensorId]: Number(event.target.value) })} min={1} max={6} style={{'width': '100px'}} />;
  };

  const onSubmit = async (data: Props) => {
    if ( model ) {
      if ( !tab.waterQualitySensorId ) {
        await createSensors(
          state.iotDeviceId,
          model.waterQualitySensorModelId,
          channel.value,
          data.waterQualityItem,
          data.alarmType,
          (data.alarmType === 'UPPER_LIMIT' || data.alarmType === 'OFF') ? null : data.alarmLowerLimit,
          (data.alarmType === 'LOWER_LIMIT' || data.alarmType === 'OFF') ? null :  data.alarmUpperLimit
        )
          .then(() => resetSensors())
          .catch(() => {
            setMessage('요청하신 수질 센서를 등록할 수 없습니다. 나중에 다시 시도해 주세요.');
          });
      } else {
        await updateSensors(
          tab.waterQualitySensorId,
          model.waterQualitySensorModelId,
          data.waterQualityItem,
          data.alarmType,
          (data.alarmType === 'UPPER_LIMIT' || data.alarmType === 'OFF') ? null : data.alarmLowerLimit,
          (data.alarmType === 'LOWER_LIMIT' || data.alarmType === 'OFF') ? null : data.alarmUpperLimit
        )
          .then(() => resetSensors())
          .catch(() => {
            setMessage('요청하신 수질 센서를 수정할 수 없습니다. 나중에 다시 시도해 주세요.');
          });
      }
    }
  };

  if ( !state ) return;
  return (
    <Container>
      <Breadcrumbs
        heading='수질 센서'
        links={[
          {name: 'IoT 장비 관리'},
          {name: 'IoT 장비', link: '/iot/devices'},
          {name: '수질 센서'}
        ]}
      />

      <StateTitle>{ state.iotGroupName } - { state.location }</StateTitle>

      <SensorFormWapper>
        <div className='box'>
          <IconButton type='button' disabled={data.length > 0 ? false : true} onClick={() => setChannelDialog(true)}><Edit />채널 수정</IconButton>
          <IconButton type='button' disabled={tabs.length > 5 ? true : false} onClick={() => handleAdd()}><Create />센서 추가</IconButton>
        </div>

        <Tabs>
          { tabs.map((item, index) => (
            <button key={index} onClick={() => handleTab(item)} className={tab.id === item.id ? 'active' : ''} style={{ width: `${100 / tabs.length}%`}}>
              {!item.waterQualitySensorId ? '----' : item.name}
            </button>
          ))}
        </Tabs>

        <Box>
          <div className='box'>
            <TertiaryButton type='button' disabled={!tab ? true : false} onClick={() => setDeleteDialog(true)}>삭제</TertiaryButton>
          </div>

          { tab ? (
            <form onSubmit={ handleSubmit(onSubmit) } noValidate>
            <SelectField
              label='수질 항목'
              name='waterQualityItem'
              control={control}
              option={[
                {value: 'PH', name: 'PH'},
                {value: 'CD', name: 'CD'},
                {value: 'RC', name: 'RC'},
                {value: 'BOD', name: 'BOD'},
                {value: 'TOC', name: 'TOC'},
                {value: 'TN', name: 'TN'},
                {value: 'TP', name: 'TP'},
              ]}
              essential
            />
              <div>
                <Label><span>* </span>수질 센서 모델</Label>
                <SelectButton type='button' id='model' onClick={() => setModelVisible(true)}>{ models.content.length > 0 && model ? `${model.name} (${model.model})` : '수질 센서 모델을 등록해 주세요.' }<div/></SelectButton>
                <Popover
                  target='#model'
                  visible={modelVisible}
                  hideOnOutsideClick={true}
                  onHiding={() => setModelVisible(false)}
                >
                  <SearchPopover>
                    { models.content.length > 0 ? (
                      models.content.map((item) => (
                        <button key={item.waterQualitySensorModelId} onClick={() => handleModel(item)}>{item.name} ( {item.model} )</button>
                      ))
                    ) : (
                      <button disabled>수질 센서 모델을 등록해 주세요.</button>
                    )}
                  </SearchPopover>
                </Popover>
              </div>

              <div>
                <Label><span>* </span>수질 센서</Label>
                <TextFields type='text' value={model?.name || ''} disabled />
              </div>

              <div>
                <Label><span>* </span>모델명</Label>
                <TextFields type='text' value={model?.model || ''} disabled />
              </div>

              <div>
                <Label><span>* </span>단위</Label>
                <TextFields type='text' value={model?.unit || ''} disabled />
              </div>

              <div>
                <Label><span>* </span>채널</Label>
                <select value={channel.value} disabled={channel.disabled} onChange={(event) => setChannel({ ...channel, value: event.target.value})}>
                  { channel.options.map((item) => (
                    <option key={item} value={item}>{ item }</option>
                  ))}
                </select>
              </div>

              <div>
                <Label><span>* </span>알람 범위</Label>

                <MultipleFields>
                  <SelectField
                    name='alarmType'
                    option={[
                      {value: 'OUT_OF_RANGE', name: '범위'},
                      {value: 'LOWER_LIMIT', name: '하한'},
                      {value: 'UPPER_LIMIT', name: '상한'},
                      {value: 'OFF', name: '알람 정지'}
                    ]}
                    control={control}
                  />

                  <div>
                    <TextField type='number' name='alarmLowerLimit' placeholder='하한' disabled={watch('alarmType') === 'UPPER_LIMIT' || watch('alarmType') === 'OFF' ? true : false} control={control} step={0.1} />
                    <p>~</p>
                    <TextField type='number' name='alarmUpperLimit' placeholder='상한' disabled={watch('alarmType') === 'LOWER_LIMIT' || watch('alarmType') === 'OFF' ? true : false} control={control} step={0.1} />
                  </div>
                </MultipleFields>

                { formMessage &&
                  <FormMessage>{ formMessage }</FormMessage>
                }
              </div>

              <Message $visible={message ? true : false}>{ message }</Message>

              <ButtonWrapper>
                <PrimaryButton type='submit'>저장</PrimaryButton>
                <TertiaryButton type='button' onClick={() => navigate('/iot/devices')}>취소</TertiaryButton>
              </ButtonWrapper>
            </form>
          ) : (
            <p style={{'textAlign': 'center', 'margin': '30px 0 40px'}}>센서를 추가해 주세요.</p>
          )}
        </Box>
      </SensorFormWapper>

      { messageDialog &&
        <Dialog
          handleClose={closeMessageDialog}
        >
          <p style={{'whiteSpace': 'pre-wrap'}}>{ messageDialog }</p>

          <PrimaryButton onClick={() => closeMessageDialog()} style={{'marginTop': '30px'}}>확인</PrimaryButton>
        </Dialog>
      }

      { addConfirm &&
        <Dialog
          handleClose={closeAddConfirm}
        >
          <p style={{'whiteSpace': 'pre-wrap'}}>{ addConfirm }</p>

          <ButtonWrapper style={{'marginTop': '30px'}}>
            <PrimaryButton onClick={() => {
              if ( tabs.filter((element) => !element.waterQualitySensorId).length > 0 ) {
                setMessageDialog('작성중인 추가 센서가 있습니다.\n저장을 완료하신 후 센서 추가를 진행해 주세요.');
              } else {
                addTab();
              }
              closeAddConfirm();
            }}>
              확인
            </PrimaryButton>
            <TertiaryButton type='button' onClick={() => closeAddConfirm()}>취소</TertiaryButton>
          </ButtonWrapper>
        </Dialog>
      }

      { tabConfirm &&
        <Dialog
          handleClose={closeTabConfirm}
        >
          <p style={{'whiteSpace': 'pre-wrap'}}>{ tabConfirm.text }</p>

          <ButtonWrapper style={{'marginTop': '30px'}}>
            <PrimaryButton onClick={() => {
              setTab(tabConfirm.value);
              closeTabConfirm();
            }}>
              확인
            </PrimaryButton>
            <TertiaryButton type='button' onClick={() => closeTabConfirm()}>취소</TertiaryButton>
          </ButtonWrapper>
        </Dialog>
      }

      { modelConfirm &&
        <Dialog
          handleClose={closeModelConfirm}
        >
          <p>
            모델을 변경하시면 작성중인 데이터는 저장이 되지 않습니다.<br />
            모델을 변경하시겠습니까?
          </p>

          <ButtonWrapper style={{'marginTop': '30px'}}>
            <PrimaryButton onClick={() => handleModelConfirm()}>확인</PrimaryButton>
            <TertiaryButton type='button' onClick={() => closeModelConfirm()}>취소</TertiaryButton>
          </ButtonWrapper>
        </Dialog>
      }

      { channelDialog &&
        <Dialog
          title='채널 수정'
          handleClose={closeChannelDialog}
        >
          <DataGrid
            dataSource={data}
            noDataText=''
            columnAutoWidth={true}
            paging={{enabled: false}}
            sorting={{ mode: 'none' }}
          >
            <Column caption='채널' dataField='channel' alignment='center' cellRender={cellRender} />
            <Column caption='수질 센서' dataField='name' alignment='center' />
          </DataGrid>

          <Message $visible={channelMessage ? true : false}>{ channelMessage }</Message>

          <ButtonWrapper>
            <PrimaryButton onClick={() => handleChannel()}>수정</PrimaryButton>
            <TertiaryButton type='button' onClick={() => setChannelDialog(false)}>취소</TertiaryButton>
          </ButtonWrapper>
        </Dialog>
      }
            
      { deleteDialog &&
        <Dialog
          handleClose={closeDeleteDialog}
        >
          <p style={{'whiteSpace': 'pre-wrap'}}>선택하신 수질 센서를 삭제하시겠습니까?</p>

          <Message $visible={deleteMessage ? true : false}>{ deleteMessage }</Message>

          <ButtonWrapper style={{'marginTop': '30px'}}>
            <PrimaryButton onClick={() => handleDelete()}>확인</PrimaryButton>
            <TertiaryButton type='button' onClick={() => closeDeleteDialog()}>취소</TertiaryButton>
          </ButtonWrapper>
        </Dialog>
      }
    </Container>
  );
}